import React, { useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Calendar, Settings, LogOut, Users, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { ROUTES } from "../config/routes";
import { useAuthStore } from "../store/authStore";

interface UserMenuProps {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
}

export function UserMenu({ isOpen: controlledIsOpen, setIsOpen: controlledSetIsOpen }: UserMenuProps = {}) {
  const navigate = useNavigate();
  const { t } = useTranslation('layout');
  const logout = useAuthStore((s) => s.logout);
  const authUser = useAuthStore((s) => s.user);
  const fetchUser = useAuthStore((s) => s.fetchUser);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const isOpen = controlledIsOpen ?? false;
  const setIsOpen = useMemo(() => controlledSetIsOpen ?? (() => {}), [controlledSetIsOpen]);

  // Fetch user data on mount to ensure we have the latest name
  useEffect(() => {
    if (authUser && (!authUser.name || authUser.name === 'User' || authUser.name.trim() === '')) {
      fetchUser().catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Use actual user data from auth store
  const user = {
    name: authUser?.name || "User",
    email: authUser?.email || "",
    avatar: authUser?.avatarUrl || null,
  };

  const handleLogout = async () => {
    setIsOpen(false);
    try {
      await logout();
      navigate('/auth/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      // Force navigation even if logout fails
      navigate('/auth/login', { replace: true });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, setIsOpen]);

  const menuItems = [
    { name: t('userMenu.profile'),   href: "/profile",                        icon: User },
    { name: t('userMenu.household'), href: `${ROUTES.SETTINGS}#household`,    icon: Users },
    { name: t('userMenu.orders'),    href: ROUTES.ORDERS,                     icon: ShoppingBag },
    { name: t('userMenu.bookings'),  href: "/bookings",                       icon: Calendar },
    { name: t('userMenu.settings'),  href: ROUTES.SETTINGS,                   icon: Settings },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Button with Avatar, Name, and Email */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2.5 h-10 pl-2 pr-3 rounded-lg border bg-background",
          "hover:bg-secondary transition-all duration-200"
        )}
      >
        {/* Avatar Circle */}
        <div className="h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-normal flex-shrink-0">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="h-full w-full rounded-full object-cover" />
          ) : (
            <span>{user.name.charAt(0)}</span>
          )}
        </div>
        
        {/* User Info */}
        <div className="flex flex-col items-start min-w-0">
          <div className="text-sm font-medium leading-tight">{user.name}</div>
          <div className="text-xs text-muted-foreground leading-tight truncate max-w-[130px]">{user.email}</div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full mt-2 right-0 min-w-[240px] z-50",
            "bg-background border rounded-lg shadow-lg",
            "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
            "duration-200"
          )}
        >
          <div className="p-2">
            {/* Menu items */}
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 rounded-md",
                    "text-sm transition-colors duration-200",
                    "hover:bg-secondary/50"
                  )}
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {item.name}
                </Link>
              );
            })}

            {/* Separator */}
            <div className="my-2 border-t" />

            {/* Logout */}
            <button
              onClick={handleLogout}
              className={cn(
                "w-full flex items-center gap-2.5 px-3 py-2 rounded-md",
                "text-sm transition-colors duration-200",
                "hover:bg-secondary/50 text-destructive hover:text-destructive"
              )}
            >
              <LogOut className="h-4 w-4" />
              {t('userMenu.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
